import { action, makeObservable, observable } from "mobx"
import { ErrorHandler } from "../../error/ErrorHandler"
import { ViewModel } from "../ViewModel"
import { CompanyExcel } from "../../../domain/entities/CompanyExcel"
import { GetCompanyExcelsUseCase } from "../../../domain/useCases/companyExcel/getCompanyExcels"
import { UploadCompanyExcelUseCase } from "../../../domain/useCases/companyExcel/uploadCompanyExcel"
import { UploadCompanyExcelDTO } from "../../../data/dto/UploadCompanyExcel.dto"

interface Props {
	ErrorHandler: ErrorHandler
	GetCompanyExcelsUseCase: GetCompanyExcelsUseCase
	UploadCompanyExcelUseCase: UploadCompanyExcelUseCase
}

export class CompanyExcelViewModel extends ViewModel {
	private _errorHandler: ErrorHandler
	private _getCompanyExcelsUseCase: GetCompanyExcelsUseCase
	private _uploadCompanyExcelUseCase: UploadCompanyExcelUseCase

	isLoading: boolean = false
	isUploading: boolean = false
	companyExcels: CompanyExcel[] = []
	isModalVisible: boolean = false

	constructor({ ErrorHandler, GetCompanyExcelsUseCase, UploadCompanyExcelUseCase }: Props) {
		super()
		makeObservable(this, {
			isLoading: observable,
			isUploading: observable,
			companyExcels: observable,
			isModalVisible: observable,
			setLoading: action,
			setUploading: action,
			setCompanyExcels: action,
			setModalVisible: action
		})
		this._errorHandler = ErrorHandler
		this._getCompanyExcelsUseCase = GetCompanyExcelsUseCase
		this._uploadCompanyExcelUseCase = UploadCompanyExcelUseCase
		this.fetchCompanyExcels()
	}

	async fetchCompanyExcels() {
		try {
			this.setLoading(true)
			const excels = await this._getCompanyExcelsUseCase.exec()
			this.setCompanyExcels(excels)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async uploadCompanyExcel(data: UploadCompanyExcelDTO): Promise<{ success: boolean; message: string }> {
		try {
			this.setUploading(true)
			const result = await this._uploadCompanyExcelUseCase.exec(data)
			if (result.success) {
				await this.fetchCompanyExcels()
			}
			return result
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setUploading(false)
		}
	}

	// Actions
	setLoading(isLoading: boolean) {
		this.isLoading = isLoading
	}

	setUploading(isUploading: boolean) {
		this.isUploading = isUploading
	}

	setCompanyExcels(excels: CompanyExcel[]) {
		this.companyExcels = excels
	}

	setModalVisible(visible: boolean) {
		this.isModalVisible = visible
	}
}
